import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as Icons from "lucide-react";
import { Reveal } from "@/components/motion";
import { WHY_CHOOSE, STATS, BRANDS, PROCESS, IMAGES } from "@/lib/data";

/* ---------------- About / Manifesto ---------------- */
export function About() {
  const chapters = [
    { n: "01", t: "Design With Intent", d: "Every millimetre is considered. We craft spaces that balance beauty, function and the way you truly live." },
    { n: "02", t: "Materials That Endure", d: "German hardware, marine-grade ply and premium finishes — engineered to look flawless for decades." },
    { n: "03", t: "Craft Over Compromise", d: "In-house manufacturing and trained craftsmen mean uncompromising quality, delivered on time." },
  ];
  return (
    <section id="about" className="py-20 md:py-32 bg-background" data-testid="about">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-4">About Smart Living</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-8">
              A design studio for the <span className="italic text-primary">modern luxury</span> home.
            </h2>
          </Reveal>
          <div className="space-y-8">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={0.1 + i * 0.1}>
                <div className="flex gap-5 group">
                  <span className="font-display text-2xl text-gold w-12 shrink-0">{c.n}</span>
                  <div className="border-l border-border pl-5">
                    <h3 className="font-display text-xl mb-1 group-hover:text-primary transition-colors">{c.t}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="img-zoom rounded-3xl overflow-hidden shadow-2xl">
              <img src={IMAGES.living2} alt="Luxury interior" loading="lazy" className="w-full h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-6 shadow-xl hidden sm:block">
              <p className="font-display text-4xl text-primary font-bold">10+</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Years of Craft</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Why Choose Us ---------------- */
export function WhyChoose() {
  return (
    <section className="py-20 md:py-28 bg-secondary/40" data-testid="why-choose">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3 text-center">Why Choose Us</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-14">The Smart Living difference</h2></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {WHY_CHOOSE.map((w, i) => {
            const Icon = Icons[w.icon] || Icons.Check;
            return (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="hover-lift bg-card rounded-2xl p-6 border border-border h-full flex flex-col items-start gap-3" data-testid={`why-${i}`}>
                  <span className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </span>
                  <h3 className="font-display text-lg">{w.title}</h3>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Animated Counter + Stats ---------------- */
function Counter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const dur = 1600;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
}

export function Stats() {
  return (
    <section className="py-20 md:py-28 bg-charcoal text-white grain relative" data-testid="stats">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10 relative z-10">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <div className="text-center">
              <p className="font-display text-5xl md:text-6xl font-bold text-primary"><Counter value={s.value} suffix={s.suffix} /></p>
              <p className="text-white/60 text-sm uppercase tracking-wider mt-2">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Brands Marquee ---------------- */
export function Brands() {
  return (
    <section className="py-16 md:py-20 bg-background border-y border-border" data-testid="brands">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-8">
        <Reveal><p className="uppercase text-xs tracking-[0.25em] text-muted-foreground text-center">Premium Hardware & Material Brands</p></Reveal>
      </div>
      <Marquee gradient={false} speed={40} pauseOnHover>
        {BRANDS.concat(BRANDS).map((b, i) => (
          <span key={i} className="mx-10 font-display text-2xl md:text-3xl text-foreground/30 hover:text-primary transition-colors cursor-default">{b}</span>
        ))}
      </Marquee>
    </section>
  );
}

/* ---------------- Process Timeline ---------------- */
export function Process() {
  return (
    <section id="process" className="py-20 md:py-32 bg-secondary/40" data-testid="process">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3 text-center">Our Process</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-16">From vision to handover</h2></Reveal>
        <div className="relative grid md:grid-cols-4 gap-8">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.06}>
              <div className="relative">
                <span className="font-display text-5xl text-primary/15 font-bold">{p.n}</span>
                <div className="w-3 h-3 rounded-full bg-primary my-3" />
                <h3 className="font-display text-lg mb-1">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
